/**
 * cleanup-helpers/firestore-diagnostics.ts
 *
 * Read-only diagnostics that query Firestore directly from the browser
 * (rather than via Cloud Functions). Each function logs a formatted report.
 *
 * Firestore SDK is loaded lazily so this file can be tree-shaken when its
 * helpers are not invoked from the production console.
 */

/**
 * Diagnostic: Check sample transaction and rule data
 */
export async function diagnoseTxRules() {
  const { getFirestore, collection, query, where, limit, getDocs } = await import('firebase/firestore');
  const { getAuth } = await import('firebase/auth');

  const db = getFirestore();
  const auth = getAuth();
  const userId = auth.currentUser?.uid;

  if (!userId) {
    console.error('❌ Not authenticated');
    return;
  }

  const txQuery = query(
    collection(db, 'transactions'),
    where('userId', '==', userId),
    where('categoryId', '==', null),
    limit(5)
  );
  const txSnapshot = await getDocs(txQuery);

  const rulesQuery = query(
    collection(db, 'rules'),
    where('userId', '==', userId),
    where('isActive', '==', true),
    where('isDeleted', '==', false),
    limit(5)
  );
  const rulesSnapshot = await getDocs(rulesQuery);

  console.log('📊 Diagnostic Results:');
  console.log('====================');

  console.log('\n🔹 Sample Transactions (5):');
  txSnapshot.docs.forEach((doc, idx) => {
    const data = doc.data();
    console.log(`  ${idx + 1}. Full transaction data:`, data);
    console.log(`       → merchant: "${data.merchant}"  |  memo: "${data.memo || ''}"`);
  });

  console.log('\n🔹 Sample Rules (5):');
  rulesSnapshot.docs.forEach((doc, idx) => {
    const data = doc.data();
    console.log(`  ${idx + 1}. field: ${data.field}  |  matchType: ${data.matchType}  |  pattern: "${data.pattern}"`);
  });

  console.log('\n📈 Statistics:');
  console.log(`  Total unclassified transactions: ${txSnapshot.size} (showing 5)`);
  console.log(`  Total active rules: ${rulesSnapshot.size} (showing 5)`);
}

/**
 * Diagnostic: Check category data structure
 */
export async function diagnoseCategories() {
  const { getFirestore, collection, query, where, limit, getDocs } = await import('firebase/firestore');
  const { getAuth } = await import('firebase/auth');

  const db = getFirestore();
  const auth = getAuth();
  const userId = auth.currentUser?.uid;

  if (!userId) {
    console.error('❌ Not authenticated');
    return;
  }

  const categoriesQuery = query(
    collection(db, 'categories'),
    where('userId', '==', userId),
    limit(10)
  );
  const categoriesSnapshot = await getDocs(categoriesQuery);

  console.log('📊 Category Diagnostic Results:');
  console.log('================================');

  console.log(`\n🔹 Total categories found: ${categoriesSnapshot.size}`);
  console.log('\n🔹 Sample Categories (up to 10):');

  categoriesSnapshot.docs.forEach((doc, idx) => {
    const data = doc.data();
    console.log(`\n  ${idx + 1}. Category ID: ${doc.id}`);
    console.log(`     name: "${data.name}"`);
    console.log(`     isDeleted: ${data.isDeleted} (type: ${typeof data.isDeleted})`);
    console.log(`     Full data:`, data);
  });

  // Check for categories with specific ID (the one showing in reports)
  const problemId = 'YCgCxDyZTijuOrfsVUBT';
  console.log(`\n🔍 Checking specific category: ${problemId}`);

  const specificQuery = query(
    collection(db, 'categories'),
    where('userId', '==', userId)
  );
  const allCategories = await getDocs(specificQuery);

  const foundCategory = allCategories.docs.find(doc => doc.id === problemId);
  if (foundCategory) {
    console.log('✅ Found category:', foundCategory.data());
  } else {
    console.log('❌ Category not found');
  }

  return {
    totalCategories: categoriesSnapshot.size,
    categories: categoriesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }))
  };
}

/**
 * Diagnostic: Check transaction data structure (Issue #3 investigation)
 * Checks merchant vs description field usage
 */
export async function diagnoseTransactions() {
  const { getFirestore, collection, query, where, limit, getDocs } = await import('firebase/firestore');
  const { getAuth } = await import('firebase/auth');

  const db = getFirestore();
  const auth = getAuth();
  const userId = auth.currentUser?.uid;

  if (!userId) {
    console.error('❌ Not authenticated');
    return;
  }

  const txQuery = query(
    collection(db, 'transactions'),
    where('userId', '==', userId),
    limit(20)
  );
  const txSnapshot = await getDocs(txQuery);

  console.log('📊 Transaction Diagnostic Results:');
  console.log('====================================');

  console.log(`\n🔹 Total transactions sampled: ${txSnapshot.size}`);
  console.log('\n🔹 Field Analysis:');

  let merchantCount = 0;
  let descriptionCount = 0;
  let bothCount = 0;
  let neitherCount = 0;

  txSnapshot.docs.forEach((doc) => {
    const data = doc.data();
    const hasMerchant = !!data.merchant;
    const hasDescription = !!data.description;

    if (hasMerchant && hasDescription) bothCount++;
    else if (hasMerchant) merchantCount++;
    else if (hasDescription) descriptionCount++;
    else neitherCount++;
  });

  console.log(`  ✓ merchant only: ${merchantCount} transactions`);
  console.log(`  ✓ description only: ${descriptionCount} transactions`);
  console.log(`  ✓ both fields: ${bothCount} transactions`);
  console.log(`  ✗ neither field: ${neitherCount} transactions`);

  console.log('\n🔹 Sample Transactions (first 5):');
  txSnapshot.docs.slice(0, 5).forEach((doc, idx) => {
    const data = doc.data();
    console.log(`\n  ${idx + 1}. Transaction ID: ${doc.id}`);
    console.log(`     date: ${data.date}`);
    console.log(`     merchant: "${data.merchant || '(empty)'}"`);
    console.log(`     merchantNorm: "${data.merchantNorm || '(empty)'}"`);
    console.log(`     description: "${data.description || '(empty)'}"`);
    console.log(`     memo: "${data.memo || '(empty)'}"`);
    console.log(`     amount: ${data.amount}`);
    console.log(`     categoryId: ${data.categoryId || 'null'}`);
  });

  console.log('\n💡 Recommendation:');
  if (descriptionCount === 0 && merchantCount > 0) {
    console.log('   ⚠️ Issue confirmed: description field is empty');
    console.log('   ➜ Solution: TransactionTable should display merchant instead of description');
  } else {
    console.log('   ✅ Both fields have data - check UI mapping');
  }

  return {
    totalSampled: txSnapshot.size,
    merchantOnly: merchantCount,
    descriptionOnly: descriptionCount,
    bothFields: bothCount,
    neitherField: neitherCount,
    samples: txSnapshot.docs.slice(0, 5).map(doc => ({
      id: doc.id,
      ...doc.data()
    }))
  };
}

/**
 * Diagnostic: Analyze rules by category to find overly broad patterns
 * Helps identify why most transactions are classified to single category
 */
export async function diagnoseRulesByCategory(targetCategoryId?: string) {
  const { getFirestore, collection, query, where, getDocs, orderBy } = await import('firebase/firestore');
  const { getAuth } = await import('firebase/auth');

  const db = getFirestore();
  const auth = getAuth();
  const userId = auth.currentUser?.uid;

  if (!userId) {
    console.error('❌ Not authenticated');
    return;
  }

  // Default to the problematic category
  const categoryId = targetCategoryId || 'YCgCxDyZTijuOrfsVUBT';

  console.log('📊 Rules Diagnostic by Category:');
  console.log('=================================');
  console.log(`\n🎯 Target Category ID: ${categoryId}`);

  const rulesQuery = query(
    collection(db, 'rules'),
    where('userId', '==', userId),
    where('categoryId', '==', categoryId),
    where('isDeleted', '==', false),
    orderBy('priority', 'desc')
  );
  const rulesSnapshot = await getDocs(rulesQuery);

  console.log(`\n🔹 Total rules found: ${rulesSnapshot.size}`);

  if (rulesSnapshot.size === 0) {
    console.log('❌ No rules found for this category');
    return;
  }

  console.log('\n🔹 Rules Analysis:');

  const ruleAnalysis = rulesSnapshot.docs.map((doc, idx) => {
    const data = doc.data();
    const patternLength = data.pattern?.length || 0;
    const isBroad = patternLength <= 3 || !data.pattern;
    const isWildcard = data.pattern?.includes('*') || data.pattern?.includes('.*');

    return {
      index: idx + 1,
      id: doc.id,
      pattern: data.pattern,
      field: data.field,
      matchType: data.matchType,
      priority: data.priority,
      isActive: data.isActive,
      patternLength,
      isBroad,
      isWildcard,
      data,
    };
  });

  ruleAnalysis.forEach((rule) => {
    console.log(`\n  ${rule.index}. Rule ID: ${rule.id}`);
    console.log(`     ✓ Pattern: "${rule.pattern}"`);
    console.log(`     ✓ Field: ${rule.field} | Match: ${rule.matchType}`);
    console.log(`     ✓ Priority: ${rule.priority} | Active: ${rule.isActive}`);
    console.log(`     ✓ Pattern Length: ${rule.patternLength}`);
    if (rule.isBroad) {
      console.log(`     ⚠️ WARNING: Pattern is very broad (length ≤ 3)`);
    }
    if (rule.isWildcard) {
      console.log(`     ⚠️ WARNING: Pattern contains wildcard`);
    }
  });

  console.log('\n📈 Summary:');
  const activeRules = ruleAnalysis.filter(r => r.isActive);
  const broadRules = ruleAnalysis.filter(r => r.isBroad);
  const wildcardRules = ruleAnalysis.filter(r => r.isWildcard);

  console.log(`  ✓ Active rules: ${activeRules.length} / ${ruleAnalysis.length}`);
  console.log(`  ⚠️ Broad patterns (≤3 chars): ${broadRules.length}`);
  console.log(`  ⚠️ Wildcard patterns: ${wildcardRules.length}`);

  console.log('\n💡 Recommendations:');
  if (broadRules.length > 0) {
    console.log(`  ⚠️ Found ${broadRules.length} rules with very broad patterns`);
    console.log('  ➜ Consider making patterns more specific');
    broadRules.forEach(r => {
      console.log(`     - Rule ${r.index}: "${r.pattern}" (${r.patternLength} chars)`);
    });
  }
  if (wildcardRules.length > 0) {
    console.log(`  ⚠️ Found ${wildcardRules.length} rules with wildcard patterns`);
    console.log('  ➜ Wildcards may match too many transactions');
  }
  if (broadRules.length === 0 && wildcardRules.length === 0) {
    console.log('  ✅ No obviously broad patterns detected');
    console.log('  ➜ Check rule priority order - highest priority rules match first');
  }

  return {
    categoryId,
    totalRules: rulesSnapshot.size,
    activeRules: activeRules.length,
    broadPatterns: broadRules.length,
    wildcardPatterns: wildcardRules.length,
    rules: ruleAnalysis,
  };
}
